/**
 * Relationship Health Page
 *
 * Workspace-wide diagnostic scan of all item relationship gate states.
 * Surfaces:
 *   - Items with blocked or flagged relationship gates (Gates 1, 2, 5 at ambient eval)
 *   - Unresolved cluster candidates (Gate 1)
 *   - Multi-frame / Reframe declaration gaps (Gate 2)
 *   - Dual-role items (Composition + Elaboration parents — Gate 5)
 *   - Package integrity blocked by unresolved clusters (Gate 1)
 *   - Active Competing (Exclusive) sets and hard Sequence constraints
 *   - Contains hierarchy integrity (reporting-only; outside the 7-gate evaluator)
 *   - Duplicate authored mirrors within Sequence or Composition
 *   - Structural conflicts (a Composition part also precedes its whole)
 *
 * Gates are evaluated at Ring 3 (continuous shaping), no bounded scope,
 * no commitment time — so Gates 3–4 and 6–7 are deferred to Collection scope
 * or commitment context (see item Relationship Gates tab).
 *
 * Per canon 07A §3 — gate priority order governs display.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Ban,
  ShieldAlert,
  AlertTriangle,
  CheckCircle2,
  GitBranch,
  ChevronDown,
  ChevronUp,
  Search,
  RefreshCw,
  Layers,
  Cpu,
  Copy,
  Zap,
  ArrowRight,
  ArrowDown,
  Swords,
  CornerDownRight,
  Tag,
  GitFork,
  Link2,
} from 'lucide-react';
import {
  isHardSequenceEdge,
  normalizeSequencePair,
} from '../../lib/sequenceRelationships';
import { normalizeCompositionPair } from '../../lib/compositionRelationships';
import { getRelationshipGuideEntry } from '../../lib/relationshipGuide';
import {
  findContainsHierarchyViolations,
  type ContainsHierarchyViolation,
} from '../../lib/containmentHierarchy';
import type { ItemRelationshipGroup } from '../../types/prioritization/itemRelationships';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useItemRelationshipsStore } from '../../stores/prioritization/itemRelationshipsStore';
import {
  evaluateItemRelationships,
  type RuleOutcome,
  type RuleOutcomeLevel,
  type RelationshipEvalContext,
} from '../../lib/itemRelationshipRuleEngine';
import type { Item } from '../../types/prioritization/lifecycle';

const SEQUENCE_GUIDE = getRelationshipGuideEntry('sequence');
const COMPOSITION_GUIDE = getRelationshipGuideEntry('composition');

function isCurrentRelationship(status: string): boolean {
  return status !== 'dissolved' && status !== 'dismissed';
}

function isActiveRelationship(status: string): boolean {
  return status === 'active' || status === 'confirmed';
}

// ============================================================================
// Level helpers
// ============================================================================

const LEVEL_ORDER: RuleOutcomeLevel[] = ['block', 'require_approval', 'warn', 'allow'];

function worstLevel(outcomes: RuleOutcome[]): RuleOutcomeLevel {
  if (outcomes.length === 0) return 'allow';
  return outcomes.reduce<RuleOutcomeLevel>((worst, o) => {
    return LEVEL_ORDER.indexOf(o.level) < LEVEL_ORDER.indexOf(worst) ? o.level : worst;
  }, 'allow');
}

const LEVEL_CONFIG: Record<RuleOutcomeLevel, {
  label: string;
  icon: React.ElementType;
  iconCls: string;
  badgeCls: string;
  rowBorder: string;
}> = {
  block:            { label: 'Blocked',  icon: Ban,          iconCls: 'text-[var(--coral)]',    badgeCls: 'bg-[var(--coral-dim)] text-[var(--coral)]',       rowBorder: 'border-l-2 border-l-red-400' },
  require_approval: { label: 'Review',   icon: ShieldAlert,  iconCls: 'text-[var(--mustard)]',  badgeCls: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',   rowBorder: 'border-l-2 border-l-amber-400' },
  warn:             { label: 'Warning',  icon: AlertTriangle,iconCls: 'text-[var(--mustard)]', badgeCls: 'bg-[var(--mustard-dim)] text-[var(--mustard)]', rowBorder: 'border-l-2 border-l-yellow-400' },
  allow:            { label: 'Clear',    icon: CheckCircle2, iconCls: 'text-[var(--green)]',badgeCls: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',rowBorder: '' },
};

/** Dark-mode-safe hover/active tints for list rows and interactive surfaces */
const LIST_ROW_HOVER = 'hover:bg-[var(--surface)]/60 dark:hover:bg-[var(--hover-tint)]';
const INTERACTIVE_HOVER = 'hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]';
const ROW_ACTIVE_BG = 'bg-[var(--surface)] dark:bg-[var(--bg-raised)]';

// ============================================================================
// Summary stats card
// ============================================================================

function StatCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-5 py-4 flex items-center gap-3">
      <div className={`p-2  ${color}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-xs text-[var(--text-ter)]">{label}</p>
        <p className="text-xl font-semibold text-[var(--text-pri)] tabular-nums leading-tight">{value}</p>
      </div>
    </div>
  );
}

// ============================================================================
// Relationship inventory strip
// ============================================================================

function InventoryPill({
  icon: Icon,
  iconCls,
  label,
  count,
  href,
}: {
  icon: React.ElementType;
  iconCls: string;
  label: string;
  count: number;
  href: string;
}) {
  return (
    <Link
      to={href}
      className={`flex items-center gap-2 px-3 py-2.5 bg-[var(--bg-raised)] border border-[var(--rule)]  hover:border-[var(--rule)] ${INTERACTIVE_HOVER} transition-colors min-w-0`}
    >
      <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${iconCls}`} />
      <span className="text-xs text-[var(--text-ter)] whitespace-nowrap">{label}</span>
      <span className="ml-auto text-sm font-semibold text-[var(--text-pri)] tabular-nums">{count}</span>
    </Link>
  );
}

// ============================================================================
// Mirror-pair row (special section for redundancy / conflict rows)
// ============================================================================

interface MirrorPairEntry {
  itemA: Item;
  itemB: Item;
  kind: 'dep_prereq' | 'agg_breakdown';
}

function MirrorPairRow({ entry }: { entry: MirrorPairEntry }) {
  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 border-b border-[var(--rule)] last:border-0 ${LIST_ROW_HOVER} border-l-2 border-l-yellow-400`}>
      <Copy className="h-3.5 w-3.5 text-[var(--mustard)] flex-shrink-0" />
      <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
        <Link
          to={`/prioritization/items/${entry.itemA.id}`}
          className="text-sm font-medium text-[var(--text-pri)] hover:text-primary-700 hover:underline truncate"
        >
          {entry.itemA.title}
        </Link>
        <ArrowRight className="h-3 w-3 text-[var(--text-ter)] flex-shrink-0" />
        <Link
          to={`/prioritization/items/${entry.itemB.id}`}
          className="text-sm font-medium text-[var(--text-pri)] hover:text-primary-700 hover:underline truncate"
        >
          {entry.itemB.title}
        </Link>
      </div>
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--mustard-dim)] text-[var(--mustard)] flex-shrink-0">
        {entry.kind === 'dep_prereq' ? 'Duplicate Sequence' : 'Duplicate Composition'}
      </span>
    </div>
  );
}

// ============================================================================
// Structural conflict row
// ============================================================================

interface ConflictEntry {
  item: Item;        // the constituent/prerequisite item
  parentItem: Item;  // the item it's both a part of AND a prereq for
}

function ConflictRow({ entry }: { entry: ConflictEntry }) {
  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 border-b border-[var(--rule)] last:border-0 ${LIST_ROW_HOVER} border-l-2 border-l-red-400`}>
      <Ban className="h-3.5 w-3.5 text-[var(--coral)] flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to={`/prioritization/items/${entry.item.id}`}
            className="text-sm font-medium text-[var(--text-pri)] hover:text-primary-700 hover:underline truncate"
          >
            {entry.item.title}
          </Link>
          <span className="text-xs text-[var(--text-ter)]">is a part of AND a prerequisite for</span>
          <Link
            to={`/prioritization/items/${entry.parentItem.id}`}
            className="text-sm font-medium text-[var(--text-pri)] hover:text-primary-700 hover:underline truncate"
          >
            {entry.parentItem.title}
          </Link>
        </div>
        <p className="text-sm text-[var(--text-ter)] font-mono mt-0.5">COMPOSITION_SEQUENCE_SEMANTIC_CONFLICT · canon 07A §4.24</p>
      </div>
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--coral-dim)] text-[var(--coral)] flex-shrink-0">
        <Ban className="h-3 w-3" />Blocked
      </span>
    </div>
  );
}

// ============================================================================
// Hard Sequence constraint row
// ============================================================================

interface SequenceConstraintEntry {
  predecessorItem: Item;
  dependentItem: Item;
  strength: 'Hard' | 'Gate';
  authoredForms: string[];
}

function SequenceConstraintRow({ entry }: { entry: SequenceConstraintEntry }) {
  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 border-b border-[var(--rule)] last:border-0 ${LIST_ROW_HOVER} border-l-2 border-l-blue-400`}>
      <GitBranch className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to={`/prioritization/items/${entry.predecessorItem.id}`}
            className="text-sm font-medium text-[var(--text-pri)] hover:text-primary-700 hover:underline truncate"
          >
            {entry.predecessorItem.title}
          </Link>
          <span className="text-xs text-[var(--text-ter)]">must be addressed before</span>
          <Link
            to={`/prioritization/items/${entry.dependentItem.id}`}
            className="text-sm font-medium text-[var(--text-pri)] hover:text-primary-700 hover:underline truncate"
          >
            {entry.dependentItem.title}
          </Link>
        </div>
        <p className="text-xs text-[var(--text-ter)] mt-0.5">
          Authored as {entry.authoredForms.join(' + ')}
        </p>
      </div>
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--slate-dim)] text-[var(--text-sec)] flex-shrink-0">
        {entry.strength} sequence
      </span>
    </div>
  );
}

// ============================================================================
// Competing (Exclusive) group row
// ============================================================================

interface ExclusiveGroupEntry {
  group: ItemRelationshipGroup;
  members: Item[];
}

function ExclusiveGroupRow({ entry }: { entry: ExclusiveGroupEntry }) {
  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 border-b border-[var(--rule)] last:border-0 ${LIST_ROW_HOVER} border-l-2 border-l-rose-300`}>
      <Swords className="h-3.5 w-3.5 text-[var(--coral)] flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to={`/prioritization/relationships/exclusives/${entry.group.id}`}
            className="text-sm font-medium text-[var(--text-pri)] hover:text-primary-700 hover:underline truncate"
          >
            {entry.group.title ?? 'Competing set'}
          </Link>
          <span className="text-xs text-[var(--text-ter)]">—</span>
          {entry.members.map((member, idx) => (
            <span key={member.id} className="inline-flex items-center gap-1">
              {idx > 0 && <span className="text-xs text-[var(--text-ter)]">·</span>}
              <Link
                to={`/prioritization/items/${member.id}`}
                className="text-sm text-[var(--text-pri)] hover:text-primary-700 hover:underline truncate max-w-[12rem]"
              >
                {member.title}
              </Link>
            </span>
          ))}
        </div>
        <p className="text-xs text-[var(--text-ter)] mt-0.5">At most one member may receive a commitment-quality outcome.</p>
      </div>
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--coral-dim)] text-[var(--coral)] flex-shrink-0">
        {entry.members.length} competing
      </span>
    </div>
  );
}

// ============================================================================
// Contains hierarchy violation row
// ============================================================================

function ContainsViolationRow({ violation, itemById }: { violation: ContainsHierarchyViolation; itemById: Map<string, Item> }) {
  if (violation.kind === 'MULTIPLE_PARENTS') {
    const child = itemById.get(violation.itemId);
    const parents = violation.parentIds
      .map((id) => itemById.get(id))
      .filter((item): item is Item => !!item);
    return (
      <div className={`flex items-center gap-3 px-4 py-2.5 border-b border-[var(--rule)] last:border-0 ${LIST_ROW_HOVER} border-l-2 border-l-amber-400`}>
        <CornerDownRight className="h-3.5 w-3.5 text-[var(--mustard)] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {child ? (
              <Link
                to={`/prioritization/items/${child.id}`}
                className="text-sm font-medium text-[var(--text-pri)] hover:text-primary-700 hover:underline truncate"
              >
                {child.title}
              </Link>
            ) : (
              <span className="text-sm font-medium text-[var(--text-pri)]">{violation.itemId}</span>
            )}
            <span className="text-xs text-[var(--text-ter)]">has multiple Contains parents:</span>
            {parents.map((parent, idx) => (
              <span key={parent.id} className="inline-flex items-center gap-1">
                {idx > 0 && <span className="text-xs text-[var(--text-ter)]">,</span>}
                <Link
                  to={`/prioritization/items/${parent.id}`}
                  className="text-sm text-[var(--text-pri)] hover:text-primary-700 hover:underline truncate max-w-[10rem]"
                >
                  {parent.title}
                </Link>
              </span>
            ))}
          </div>
          <p className="text-sm text-[var(--text-ter)] font-mono mt-0.5">CONTAINS_SINGLE_PARENT · reporting hierarchy</p>
        </div>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--mustard-dim)] text-[var(--mustard)] flex-shrink-0">
          Multiple parents
        </span>
      </div>
    );
  }

  const parent = itemById.get(violation.sourceItemId);
  const child = itemById.get(violation.targetItemId);
  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 border-b border-[var(--rule)] last:border-0 ${LIST_ROW_HOVER} border-l-2 border-l-amber-400`}>
      <CornerDownRight className="h-3.5 w-3.5 text-[var(--mustard)] flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {parent ? (
            <Link
              to={`/prioritization/items/${parent.id}`}
              className="text-sm font-medium text-[var(--text-pri)] hover:text-primary-700 hover:underline truncate"
            >
              {parent.title}
            </Link>
          ) : (
            <span className="text-sm font-medium text-[var(--text-pri)]">{violation.sourceItemId}</span>
          )}
          <span className="text-xs text-[var(--text-ter)]">→</span>
          {child ? (
            <Link
              to={`/prioritization/items/${child.id}`}
              className="text-sm font-medium text-[var(--text-pri)] hover:text-primary-700 hover:underline truncate"
            >
              {child.title}
            </Link>
          ) : (
            <span className="text-sm font-medium text-[var(--text-pri)]">{violation.targetItemId}</span>
          )}
          <span className="text-xs text-[var(--text-ter)]">forms a cycle in the Contains tree</span>
        </div>
        <p className="text-sm text-[var(--text-ter)] font-mono mt-0.5">CONTAINS_CYCLE · reporting hierarchy</p>
      </div>
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--mustard-dim)] text-[var(--mustard)] flex-shrink-0">
        Cycle
      </span>
    </div>
  );
}

// ============================================================================
// Item health row
// ============================================================================

interface ItemHealthEntry {
  item: Item;
  outcomes: RuleOutcome[];
  level: RuleOutcomeLevel;
}

function ItemHealthRow({ entry }: { entry: ItemHealthEntry }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = LEVEL_CONFIG[entry.level];
  const LevelIcon = cfg.icon;
  const hasIssues = entry.outcomes.length > 0 && entry.level !== 'allow';

  // Group outcomes by gate
  const byGate = useMemo(() => {
    const map = new Map<number, RuleOutcome[]>();
    for (const o of entry.outcomes) {
      if (!map.has(o.gate)) map.set(o.gate, []);
      map.get(o.gate)!.push(o);
    }
    return [...map.entries()].sort((a, b) => a[0] - b[0]);
  }, [entry.outcomes]);

  return (
    <div className={`group border-b border-[var(--rule)] last:border-0 ${cfg.rowBorder}`}>
      <div
        className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${
          expanded ? ROW_ACTIVE_BG : LIST_ROW_HOVER
        }`}
      >
        {/* Expand toggle */}
        <button
          type="button"
          onClick={() => hasIssues && setExpanded((v) => !v)}
          className={`flex-shrink-0 p-0.5 rounded transition-colors ${
            hasIssues ? 'text-[var(--text-ter)] hover:text-[var(--text-sec)] cursor-pointer' : 'text-transparent cursor-default'
          }`}
        >
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>

        {/* Title */}
        <div className="flex-1 min-w-0">
          <Link
            to={`/prioritization/items/${entry.item.id}`}
            className="text-sm font-medium text-[var(--text-pri)] hover:text-primary-700 hover:underline truncate block"
          >
            {entry.item.title}
          </Link>
        </div>

        {/* Category */}
        {entry.item.itemCategory && (
          <span className="hidden sm:inline-flex text-xs px-2 py-0.5 rounded-full bg-[var(--hover-tint)] text-[var(--text-sec)] capitalize whitespace-nowrap flex-shrink-0">
            {entry.item.itemCategory}
          </span>
        )}

        {/* Level badge */}
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${cfg.badgeCls}`}>
          <LevelIcon className="h-3 w-3" />
          {cfg.label}
          {entry.outcomes.length > 0 && entry.level !== 'allow' && (
            <span className="opacity-70">({entry.outcomes.length})</span>
          )}
        </span>

        <Link
          to={`/prioritization/items/${entry.item.id}?tab=relationships`}
          className="p-1 text-[var(--text-ter)] hover:text-primary-600 flex-shrink-0 transition-colors"
          title="View relationships"
        >
          <GitBranch className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* Expanded detail */}
      {expanded && byGate.length > 0 && (
        <div className="px-10 pb-3 space-y-2">
          {byGate.map(([gate, gateOutcomes]) => (
            <div key={gate}>
              <p className="text-xs font-semibold text-[var(--text-ter)] mb-1">Gate {gate}</p>
              <div className="space-y-1">
                {gateOutcomes.map((o, idx) => {
                  const oCfg = LEVEL_CONFIG[o.level];
                  const OIcon = oCfg.icon;
                  return (
                    <div key={idx} className="flex items-start gap-2 bg-[var(--bg-raised)] border border-[var(--rule)] px-3 py-2">
                      <OIcon className={`h-3.5 w-3.5 flex-shrink-0 mt-0.5 ${oCfg.iconCls}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-[var(--text-sec)]">{o.message}</p>
                        <p className="text-sm text-[var(--text-ter)] font-mono mt-0.5">{o.reason}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <Link
            to={`/prioritization/items/${entry.item.id}?tab=relationships`}
            className="inline-flex items-center gap-1 text-xs text-primary-600 hover:underline mt-1"
          >
            <GitBranch className="h-3 w-3" />
            Open relationships for this item
          </Link>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Health section (a grouped list of item health entries)
// ============================================================================

function HealthSection({
  title,
  icon: Icon,
  iconBg,
  description,
  entries,
  emptyMessage,
  defaultOpen,
  children,
}: {
  title: string;
  icon: React.ElementType;
  iconBg: string;
  description: string;
  entries?: ItemHealthEntry[];
  emptyMessage: string;
  defaultOpen?: boolean;
  children?: React.ReactNode;
}) {
  const issueCount = (entries ?? []).filter((e) => e.level !== 'allow').length;
  const totalCount = entries !== undefined ? entries.length : -1;
  const [open, setOpen] = useState(defaultOpen ?? (totalCount > 0));

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center gap-3 px-4 py-3.5 ${INTERACTIVE_HOVER} transition-colors text-left`}
      >
        <div className={`p-1.5  ${iconBg} flex-shrink-0`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-[var(--text-pri)]">{title}</span>
            {issueCount > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-[var(--coral-dim)] text-[var(--coral)]">
                <Ban className="h-3 w-3" />{issueCount} issues
              </span>
            )}
            {issueCount === 0 && totalCount > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]">
                <CheckCircle2 className="h-3 w-3" />All clear
              </span>
            )}
            {totalCount >= 0 && (
              <span className="text-xs text-[var(--text-ter)]">{totalCount} item{totalCount !== 1 ? 's' : ''}</span>
            )}
          </div>
          <p className="text-xs text-[var(--text-ter)] mt-0.5 truncate">{description}</p>
        </div>
        <ChevronDown
          className={`h-4 w-4 text-[var(--text-ter)] flex-shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="border-t border-[var(--rule)]">
          {children ? (
            children
          ) : entries && entries.length === 0 ? (
            <div className="py-8 text-center text-[var(--text-ter)]">
              <CheckCircle2 className="h-6 w-6 mx-auto mb-2 text-[var(--green)]" />
              <p className="text-sm">{emptyMessage}</p>
            </div>
          ) : (
            <div className="divide-y divide-[var(--rule)]">
              {(entries ?? []).map((e) => (
                <ItemHealthRow key={e.item.id} entry={e} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main page
// ============================================================================

export default function RelationshipHealthPage() {
  const [searchParams] = useSearchParams();
  const initialLevel = searchParams.get('level');
  const initialLevelFilter: RuleOutcomeLevel | 'all' =
    initialLevel === 'block' ||
    initialLevel === 'require_approval' ||
    initialLevel === 'warn' ||
    initialLevel === 'allow'
      ? initialLevel
      : 'all';

  const items = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const allGroups = useItemRelationshipsStore((s) => s.groups);
  const allMemberships = useItemRelationshipsStore((s) => s.groupMemberships);
  const allEdges = useItemRelationshipsStore((s) => s.edges);
  const fetchRelationships = useItemRelationshipsStore((s) => s.fetchFromSupabase);

  const [query, setQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState<RuleOutcomeLevel | 'all'>(initialLevelFilter);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    void Promise.all([fetchItems(), fetchRelationships()]);
  }, [fetchItems, fetchRelationships]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchItems(), fetchRelationships()]);
    setRefreshing(false);
  };

  // Item lookup map
  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const currentEdges = useMemo(
    () => allEdges.filter((edge) => isCurrentRelationship(edge.status)),
    [allEdges],
  );
  const currentGroups = useMemo(
    () => allGroups.filter((group) => isCurrentRelationship(group.status)),
    [allGroups],
  );
  const activeEdges = useMemo(
    () => allEdges.filter((edge) => isActiveRelationship(edge.status)),
    [allEdges],
  );
  const activeGroups = useMemo(
    () => allGroups.filter((group) => isActiveRelationship(group.status)),
    [allGroups],
  );

  // Evaluate every item at ambient gate state (Ring 3, no scope, no frame)
  const allEntries = useMemo((): ItemHealthEntry[] => {
    return items.map((item) => {
      // Use the item's own canonical frame if one is set, so Gate 2 doesn't
      // fire false positives for items that already have an active frame declared.
      const canonicalFrame = (item.frames ?? []).find((f) => f.isCanonical);
      const ctx: RelationshipEvalContext = {
        itemId: item.id,
        allGroups: currentGroups,
        allMemberships,
        allEdges: currentEdges,
        itemFrames: item.frames ?? [],
        scopeItemIds: null,
        activeFrameId: canonicalFrame?.id ?? null,
        // If no alternate frame is canonical, the item's own category is the active framing.
        itemOwnFrameIsCanonical: !canonicalFrame && !!item.itemCategory,
        declaredTrackRole: null,
        selectedElaborationChildId: null,
        ringAtTime: 3,
        isCommitmentTime: false,
      };
      const result = evaluateItemRelationships(ctx);
      const nonAllowOutcomes = result.outcomes.filter((o) => o.level !== 'allow');
      return {
        item,
        outcomes: nonAllowOutcomes,
        level: worstLevel(nonAllowOutcomes),
      };
    });
  }, [items, currentGroups, allMemberships, currentEdges]);

  // Filter helpers
  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return allEntries.filter((e) => {
      if (levelFilter !== 'all' && e.level !== levelFilter) return false;
      if (q && !e.item.title?.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [allEntries, query, levelFilter]);

  // ── Gate-based sections ────────────────────────────────────────────────────

  const blockedEntries = useMemo(
    () => filtered.filter((e) => e.level === 'block').sort((a, b) => b.outcomes.length - a.outcomes.length),
    [filtered],
  );

  const clusterEntries = useMemo(
    () => filtered.filter((e) => e.outcomes.some((o) => o.gate === 1 && o.reason === 'CLUSTER_REVIEW_REQUIRED')),
    [filtered],
  );

  const frameDeclarationEntries = useMemo(
    () => filtered.filter((e) => e.outcomes.some((o) => o.gate === 2)),
    [filtered],
  );

  const packageBlockedEntries = useMemo(
    () => filtered.filter((e) => e.outcomes.some((o) => o.reason === 'CLUSTER_BLOCKS_PACKAGE')),
    [filtered],
  );

  const dualRoleEntries = useMemo(
    () => filtered.filter((e) => e.outcomes.some((o) => o.gate === 5)),
    [filtered],
  );

  // ── Hard Sequence constraints (normalized predecessor → dependent) ────────
  // Dependency and Prerequisite are authored mirrors. Show each semantic pair
  // once while preserving direction and authorship details.

  const sequenceConstraintEntries = useMemo((): SequenceConstraintEntry[] => {
    const byPair = new Map<string, SequenceConstraintEntry>();
    const q = query.toLowerCase().trim();
    for (const edge of activeEdges) {
      if (!isHardSequenceEdge(edge)) continue;
      const pair = normalizeSequencePair(edge);
      if (!pair) continue;
      const predecessorItem = itemById.get(pair.predecessorItemId);
      const dependentItem = itemById.get(pair.dependentItemId);
      if (!predecessorItem || !dependentItem) continue;
      if (
        q &&
        !predecessorItem.title?.toLowerCase().includes(q) &&
        !dependentItem.title?.toLowerCase().includes(q) &&
        !edge.type.toLowerCase().includes(q)
      ) {
        continue;
      }

      const key = `${pair.predecessorItemId}:${pair.dependentItemId}`;
      const authoredForm = edge.type === 'prerequisite' ? 'prerequisite' : 'dependency';
      const existing = byPair.get(key);
      if (existing) {
        if (!existing.authoredForms.includes(authoredForm)) existing.authoredForms.push(authoredForm);
        if (edge.subtype === 'gate') existing.strength = 'Gate';
      } else {
        byPair.set(key, {
          predecessorItem,
          dependentItem,
          strength: edge.subtype === 'gate' ? 'Gate' : 'Hard',
          authoredForms: [authoredForm],
        });
      }
    }
    return [...byPair.values()];
  }, [activeEdges, itemById, query]);

  // ── Competing (Exclusive) sets ─────────────────────────────────────────────

  const exclusiveGroupEntries = useMemo((): ExclusiveGroupEntry[] => {
    const q = query.toLowerCase().trim();
    return activeGroups
      .filter((g) => g.type === 'exclusive')
      .map((group) => {
        const memberIds = allMemberships
          .filter((m) => m.groupId === group.id)
          .map((m) => m.itemId);
        const members = memberIds
          .map((id) => itemById.get(id))
          .filter((item): item is Item => !!item);
        return { group, members };
      })
      .filter((entry) => entry.members.length >= 2)
      .filter((entry) => {
        if (!q) return true;
        const groupMatch = entry.group.title?.toLowerCase().includes(q);
        const memberMatch = entry.members.some((m) => m.title?.toLowerCase().includes(q));
        return groupMatch || memberMatch;
      });
  }, [activeGroups, allMemberships, itemById, query]);

  // ── Contains hierarchy violations ─────────────────────────────────────────

  const containsViolationEntries = useMemo((): ContainsHierarchyViolation[] => {
    const violations = findContainsHierarchyViolations(activeEdges);
    const q = query.toLowerCase().trim();
    if (!q) return violations;
    return violations.filter((v) => {
      if (v.kind === 'MULTIPLE_PARENTS') {
        const child = itemById.get(v.itemId);
        const parentMatch = v.parentIds.some((id) => itemById.get(id)?.title?.toLowerCase().includes(q));
        return child?.title?.toLowerCase().includes(q) || parentMatch;
      }
      const source = itemById.get(v.sourceItemId);
      const target = itemById.get(v.targetItemId);
      return (
        source?.title?.toLowerCase().includes(q) ||
        target?.title?.toLowerCase().includes(q) ||
        false
      );
    });
  }, [activeEdges, itemById, query]);

  // ── Duplicate authored mirrors ─────────────────────────────────────────────
  // (a) Dep + Prereq on same pair: B depends on A (dep: B→A) AND A is prereq for B (pre: A→B)
  // (b) Agg + Breakdown on the same normalized whole → part pair

  const mirrorPairEntries = useMemo((): MirrorPairEntry[] => {
    const results: MirrorPairEntry[] = [];
    const q = query.toLowerCase().trim();
    const seen = new Set<string>();

    const sequenceFormsByPair = new Map<
      string,
      { predecessorItemId: string; dependentItemId: string; forms: Set<string> }
    >();
    for (const edge of activeEdges) {
      const normalized = normalizeSequencePair(edge);
      if (!normalized) continue;
      const key = `sequence:${normalized.predecessorItemId}:${normalized.dependentItemId}`;
      const pair = sequenceFormsByPair.get(key) ?? {
        predecessorItemId: normalized.predecessorItemId,
        dependentItemId: normalized.dependentItemId,
        forms: new Set<string>(),
      };
      pair.forms.add(normalized.edgeType);
      sequenceFormsByPair.set(key, pair);
    }
    for (const [key, pair] of sequenceFormsByPair) {
      if (!pair.forms.has('dependency') || !pair.forms.has('prerequisite')) continue;
      seen.add(key);
      const predecessor = itemById.get(pair.predecessorItemId);
      const dependent = itemById.get(pair.dependentItemId);
      if (!predecessor || !dependent) continue;
      if (q && !predecessor.title?.toLowerCase().includes(q) && !dependent.title?.toLowerCase().includes(q)) continue;
      results.push({ itemA: predecessor, itemB: dependent, kind: 'dep_prereq' });
    }

    const aggEdges = activeEdges.filter((e) => e.type === 'aggregation');
    const bdEdges  = activeEdges.filter((e) => e.type === 'breakdown');
    for (const agg of aggEdges) {
      const matchingBd = bdEdges.find(
        (bd) => bd.sourceItemId === agg.sourceItemId && bd.targetItemId === agg.targetItemId,
      );
      if (!matchingBd) continue;
      const itemA = itemById.get(agg.sourceItemId);
      const itemB = itemById.get(agg.targetItemId);
      if (!itemA || !itemB) continue;
      const key = `composition:${agg.sourceItemId}:${agg.targetItemId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      if (q && !itemA.title?.toLowerCase().includes(q) && !itemB.title?.toLowerCase().includes(q)) continue;
      results.push({ itemA, itemB, kind: 'agg_breakdown' });
    }

    return results;
  }, [activeEdges, itemById, query]);

  // ── Structural conflicts (Composition + Sequence) ──────────────────────────
  // Item C is a Composition part of P and also precedes P in Sequence.
  // A constituent part cannot also be a precondition for the whole it composes.

  const structuralConflictEntries = useMemo((): ConflictEntry[] => {
    const results: ConflictEntry[] = [];
    const q = query.toLowerCase().trim();
    const hardSequencePairs = activeEdges
      .filter(isHardSequenceEdge)
      .map(normalizeSequencePair)
      .filter((pair): pair is NonNullable<typeof pair> => pair !== null);
    const seenPairs = new Set<string>();

    for (const edge of activeEdges) {
      const composition = normalizeCompositionPair(edge);
      if (!composition) continue;
      const conflicts = hardSequencePairs.some(
        (sequence) =>
          sequence.predecessorItemId === composition.partItemId &&
          sequence.dependentItemId === composition.wholeItemId,
      );
      if (!conflicts) continue;
      const pairKey = `${composition.wholeItemId}:${composition.partItemId}`;
      if (seenPairs.has(pairKey)) continue;
      seenPairs.add(pairKey);
      const conflictItem = itemById.get(composition.partItemId);
      const parentItem = itemById.get(composition.wholeItemId);
      if (!conflictItem || !parentItem) continue;
      if (q && !conflictItem.title?.toLowerCase().includes(q) && !parentItem.title?.toLowerCase().includes(q)) continue;
      results.push({ item: conflictItem, parentItem });
    }
    return results;
  }, [activeEdges, itemById, query]);

  // ── Relationship inventory ─────────────────────────────────────────────────

  const inventoryCounts = useMemo(() => {
    const sequencePairs = new Set<string>();
    const compositionPairs = new Set<string>();
    for (const edge of activeEdges) {
      const sequence = normalizeSequencePair(edge);
      if (sequence) {
        sequencePairs.add(`${sequence.predecessorItemId}:${sequence.dependentItemId}`);
      }
      const composition = normalizeCompositionPair(edge);
      if (composition) {
        compositionPairs.add(`${composition.wholeItemId}:${composition.partItemId}`);
      }
    }
    return {
      sequence: sequencePairs.size,
      composition: compositionPairs.size,
      contains: activeEdges.filter((e) => e.type === 'contains').length,
      elaboration: activeEdges.filter((e) => e.type === 'elaboration').length,
      reframes: items.filter((i) => (i.frames ?? []).length > 1).length,
      packages: activeGroups.filter((g) => g.type === 'package').length,
      exclusives: activeGroups.filter((g) => g.type === 'exclusive').length,
      clusters: activeGroups.filter((g) => g.type === 'cluster').length,
      collections: activeGroups.filter((g) => g.type === 'collection').length,
    };
  }, [activeEdges, activeGroups, items]);

  // ── Aggregate counts ───────────────────────────────────────────────────────

  const blockedCount = allEntries.filter((e) => e.level === 'block').length;
  const reviewCount  = allEntries.filter((e) => e.level === 'require_approval').length;
  const warnCount    = allEntries.filter((e) => e.level === 'warn').length;
  const clearCount   = allEntries.filter((e) => e.level === 'allow').length;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<GitBranch className="w-4 h-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Relationship Health"
        subtitle="Workspace-wide diagnostics across the current relationship model — gate blocks, directed Sequence constraints, Composition integrity, Competing sets, and reporting hierarchy."
        actions={
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)]  ${INTERACTIVE_HOVER} disabled:opacity-50 transition-colors`}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      {/* Gate-level summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Ban}          label="Blocked"  value={blockedCount} color="bg-[var(--coral-dim)] text-[var(--coral)]" />
        <StatCard icon={ShieldAlert}  label="Review"   value={reviewCount}  color="bg-[var(--mustard-dim)] text-[var(--mustard)]" />
        <StatCard icon={AlertTriangle}label="Warning"  value={warnCount}    color="bg-[var(--mustard-dim)] text-[var(--mustard)]" />
        <StatCard icon={CheckCircle2} label="Clear"    value={clearCount}   color="bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]" />
      </div>

      {/* Relationship inventory */}
      <div>
        <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mb-2">Active relationships</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
          <InventoryPill icon={SEQUENCE_GUIDE?.Icon ?? GitBranch}       iconCls={SEQUENCE_GUIDE?.iconColor ?? 'text-[var(--text-ter)]'}     label="Sequence"      count={inventoryCounts.sequence}     href="/prioritization/relationships/sequence" />
          <InventoryPill icon={COMPOSITION_GUIDE?.Icon ?? ArrowDown}    iconCls={COMPOSITION_GUIDE?.iconColor ?? 'text-[var(--green)] dark:text-[var(--text-ter)]'}  label="Composition"   count={inventoryCounts.composition}  href="/prioritization/relationships/composition" />
          <InventoryPill icon={CornerDownRight} iconCls="text-[var(--text-ter)]"      label="Contains"      count={inventoryCounts.contains}     href="/prioritization/relationships/contains" />
          <InventoryPill icon={GitFork}         iconCls="text-[var(--text-ter)]"   label="Elaborations"  count={inventoryCounts.elaboration}  href="/prioritization/relationships/elaborations" />
          <InventoryPill icon={RefreshCw}       iconCls="text-[var(--coral)]"     label="Additional Categories" count={inventoryCounts.reframes}     href="/prioritization/relationships/reframes" />
          <InventoryPill icon={Layers}          iconCls="text-[var(--mustard)]"   label="Packages"      count={inventoryCounts.packages}     href="/prioritization/relationships/packages" />
          <InventoryPill icon={Swords}          iconCls="text-[var(--coral)]"     label="Competing"     count={inventoryCounts.exclusives}   href="/prioritization/relationships/exclusives" />
          <InventoryPill icon={Tag}             iconCls="text-cyan-600"     label="Clusters"      count={inventoryCounts.clusters}     href="/prioritization/relationships/clusters" />
          <InventoryPill icon={Link2}           iconCls="text-[var(--text-ter)]"   label="Collections"   count={inventoryCounts.collections}  href="/prioritization/collections" />
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
          <input
            type="text"
            placeholder="Filter by item title…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-primary-400/40 focus:border-primary-400"
          />
        </div>
        <div className="flex items-center border border-[var(--rule)] overflow-hidden text-xs">
          {(['all', 'block', 'require_approval', 'warn', 'allow'] as const).map((lvl) => {
            const label = lvl === 'all' ? 'All' : lvl === 'require_approval' ? 'Review' : lvl.charAt(0).toUpperCase() + lvl.slice(1);
            return (
              <button
                key={lvl}
                type="button"
                onClick={() => setLevelFilter(lvl)}
                className={`px-3 py-1.5 font-medium transition-colors border-r border-[var(--rule)] last:border-0 ${
                  levelFilter === lvl ? 'bg-[var(--hover-tint)] text-[var(--text-pri)]' : `text-[var(--text-ter)] ${INTERACTIVE_HOVER}`
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-4">

        {/* Hard gate blocks */}
        <HealthSection
          title="Blocked items"
          icon={Ban}
          iconBg="bg-[var(--coral-dim)] text-[var(--coral)]"
          description="Hard stops — these items cannot enter a Track or be committed until the gate issue is resolved. Click the gate icon on any row to see exactly what's wrong and how to fix it."
          entries={blockedEntries}
          emptyMessage="No blocked items. All gates clear at ambient evaluation."
          defaultOpen
        />

        {/* Structural conflicts — Composition part precedes its whole */}
        <HealthSection
          title="Structural conflicts"
          icon={Zap}
          iconBg="bg-[var(--coral-dim)] text-[var(--coral)]"
          description="An item is both a constituent part in Composition and a hard predecessor of that same whole in Sequence. These roles are semantically incompatible — a part cannot precede the whole it composes. Canon 07A §4.24."
          emptyMessage="No structural conflicts found."
          defaultOpen={structuralConflictEntries.length > 0}
        >
          {structuralConflictEntries.length === 0 ? (
            <div className="py-8 text-center text-[var(--text-ter)]">
              <CheckCircle2 className="h-6 w-6 mx-auto mb-2 text-[var(--green)]" />
              <p className="text-sm">No structural conflicts found.</p>
            </div>
          ) : (
            <div>
              {structuralConflictEntries.map((entry, i) => (
                <ConflictRow key={i} entry={entry} />
              ))}
            </div>
          )}
        </HealthSection>

        {/* Contains hierarchy integrity (outside 7-gate evaluator) */}
        <HealthSection
          title="Contains hierarchy issues"
          icon={CornerDownRight}
          iconBg="bg-[var(--mustard-dim)] text-[var(--mustard)]"
          description="Contains is reporting-only and does not participate in Gates 1–7, but the tree must stay acyclic with at most one parent per item. Violations here indicate corrupted hierarchy data that should be re-homed. Canon 07A §3.1."
          emptyMessage="Contains hierarchy is valid — no cycles or multiple parents."
          defaultOpen={containsViolationEntries.length > 0}
        >
          {containsViolationEntries.length === 0 ? (
            <div className="py-8 text-center text-[var(--text-ter)]">
              <CheckCircle2 className="h-6 w-6 mx-auto mb-2 text-[var(--green)]" />
              <p className="text-sm">Contains hierarchy is valid — no cycles or multiple parents.</p>
            </div>
          ) : (
            <div>
              {containsViolationEntries.map((violation, i) => (
                <ContainsViolationRow key={i} violation={violation} itemById={itemById} />
              ))}
            </div>
          )}
        </HealthSection>

        {/* Cluster issues — Gate 1 */}
        <HealthSection
          title="Unresolved cluster candidates"
          icon={Tag}
          iconBg="bg-cyan-50 text-cyan-600"
          description="This item has been flagged as a potential duplicate or variant of another item but hasn't been resolved yet. Gate 1 blocks all downstream gates — nothing else can be assessed until cluster membership is decided."
          entries={clusterEntries}
          emptyMessage="No unresolved cluster issues."
        />

        {/* Multi-frame / Reframe — Gate 2 */}
        <HealthSection
          title="Items with more than one category needing declaration"
          icon={RefreshCw}
          iconBg="bg-[var(--coral-dim)] text-[var(--coral)]"
          description="Items with more than one category but no active category declared for this context. Gate 2 requires an active category before Track entry."
          entries={frameDeclarationEntries}
          emptyMessage="Every item with more than one category has an active category declared."
          defaultOpen={frameDeclarationEntries.length > 0}
        />

        {/* Package blocked by cluster — Gate 1 */}
        <HealthSection
          title="Packages blocked by unresolved clusters"
          icon={Layers}
          iconBg="bg-[var(--mustard-dim)] text-[var(--mustard)]"
          description="A Package member is still in an unresolved Cluster. The Package cannot enter a Track or receive commitment-quality outcomes until cluster identity is resolved. Canon 07A Rule CL-BL-1."
          entries={packageBlockedEntries}
          emptyMessage="No Packages are blocked by cluster ambiguity."
        />

        {/* Dual-role declaration — Gate 5 */}
        <HealthSection
          title="Dual-role items requiring declaration"
          icon={Cpu}
          iconBg="bg-[var(--mustard-dim)] text-[var(--mustard)]"
          description="This item is both a Composition whole and an Elaboration parent. Its track role must be declared before Track entry: composition (structural roll-up) or elaboration (a need plus its possibility continuum). Gate 5."
          entries={dualRoleEntries}
          emptyMessage="No items requiring track role declaration."
        />

        {/* Competing (Exclusive) sets */}
        <HealthSection
          title="Competing sets"
          icon={Swords}
          iconBg="bg-[var(--coral-dim)] text-[var(--coral)]"
          description="Active Competing (Exclusive) groups in the workspace. Multiple members in the same scope is expected — at commitment time (Gate 4), at most one may receive a commitment-quality outcome (“one or the other, not both”)."
          emptyMessage="No Competing sets with two or more members."
          defaultOpen={exclusiveGroupEntries.length > 0}
        >
          {exclusiveGroupEntries.length === 0 ? (
            <div className="py-8 text-center text-[var(--text-ter)]">
              <CheckCircle2 className="h-6 w-6 mx-auto mb-2 text-[var(--green)]" />
              <p className="text-sm">No Competing sets with two or more members.</p>
            </div>
          ) : (
            <div>
              {exclusiveGroupEntries.map((entry) => (
                <ExclusiveGroupRow key={entry.group.id} entry={entry} />
              ))}
            </div>
          )}
        </HealthSection>

        {/* Hard Sequence constraints, normalized and role-aware */}
        <HealthSection
          title="Hard Sequence constraints"
          icon={SEQUENCE_GUIDE?.Icon ?? GitBranch}
          iconBg="bg-[var(--slate-dim)] text-[var(--text-sec)]"
          description="Each row reads predecessor → dependent, regardless of whether the link was authored as a Dependency or Prerequisite. Mirror assertions are counted once. Hard and gate-strength links can block scope entry or commitment."
          emptyMessage="No active hard or gate-strength Sequence constraints."
          defaultOpen={sequenceConstraintEntries.length > 0}
        >
          {sequenceConstraintEntries.length === 0 ? (
            <div className="py-8 text-center text-[var(--text-ter)]">
              <CheckCircle2 className="h-6 w-6 mx-auto mb-2 text-[var(--green)]" />
              <p className="text-sm">No active hard or gate-strength Sequence constraints.</p>
            </div>
          ) : (
            <div>
              {sequenceConstraintEntries.map((entry) => (
                <SequenceConstraintRow
                  key={`${entry.predecessorItem.id}:${entry.dependentItem.id}`}
                  entry={entry}
                />
              ))}
            </div>
          )}
        </HealthSection>

        {/* Duplicate authored mirrors */}
        <HealthSection
          title="Duplicate mirror assertions"
          icon={Copy}
          iconBg="bg-[var(--mustard-dim)] text-[var(--mustard)]"
          description="The same semantic link was stored twice through both authored mirror forms: Dependency + Prerequisite for one Sequence, or Aggregation + Breakdown for one Composition. Inventory totals already fold these records into one relationship; dissolve one stored assertion to remove the redundancy."
          emptyMessage="No duplicate mirror assertions found."
          defaultOpen={mirrorPairEntries.length > 0}
        >
          {mirrorPairEntries.length === 0 ? (
            <div className="py-8 text-center text-[var(--text-ter)]">
              <CheckCircle2 className="h-6 w-6 mx-auto mb-2 text-[var(--green)]" />
              <p className="text-sm">No duplicate mirror assertions found.</p>
            </div>
          ) : (
            <div>
              {mirrorPairEntries.map((entry, i) => (
                <MirrorPairRow key={i} entry={entry} />
              ))}
            </div>
          )}
        </HealthSection>

      </div>
    </div>
  );
}
